import React, { CSSProperties, FunctionComponent } from "react";
import { useResizeObserver } from "designSystem/hooks";
import Box, { BoxProps } from "./Box";

export interface AspectBoxProps extends BoxProps {
	/** aspect ratio which will be multiplied by one of the dimensions to get the other (defaults to 1) */
	aspectRatio?: number;

	/** indicates if aspect ratio is multiple of height (rather than width which is default) */
	byHeight?: boolean;

	/** properties to apply to the inner box that will contain the given children */
	innerBoxProps?: Partial<BoxProps>;

	/** indicates desired resolution of main box dimension (if not 1-to-1 with pixel height) */
	resolution?: number;
}

const AspectBox: FunctionComponent<AspectBoxProps> = (props) => {
	const {
		height: resultHeight,
		width: resultWidth,
		ref,
	} = useResizeObserver<HTMLDivElement>();

	const {
		aspectRatio,
		byHeight,
		children,
		height,
		innerBoxProps,
		resolution,
		width,
		...otherProps
	} = props;

	const hasSize = byHeight ? !!resultHeight : !!resultWidth;
	const finalAspectRatio = aspectRatio || 1;
	let aspectBoxChildren = children;
	let aspectHeight = resultHeight;
	let aspectWidth = resultWidth;
	let style: CSSProperties;

	if (hasSize) {
		const dim = byHeight ? resultHeight : resultWidth;
		const otherDim = dim * finalAspectRatio;

		if (byHeight) {
			aspectWidth = Math.trunc(otherDim);

			if (aspectWidth > resultWidth) {
				// if height is driving width, then ensure we stay under any specified maximum width
				aspectWidth = resultWidth;
				aspectHeight = Math.trunc(aspectWidth / finalAspectRatio);
			}
		} else {
			aspectHeight = Math.trunc(otherDim);

			if (aspectHeight > resultHeight) {
				// if width is driving height, then ensure we stay under any specified maximum height
				aspectHeight = resultHeight;
				aspectWidth = Math.trunc(aspectHeight / finalAspectRatio);
			}
		}

		if (resolution) {
			style = {
				transform: `scale(${dim / resolution})`,
				transformOrigin: "top left",
			};
		}
	} else {
		aspectBoxChildren = null;
	}

	return (
		<Box
			height={height || "100%"}
			width={width || "100%"}
			{...{ ref }}
			{...otherProps}
		>
			{hasSize && (
				<Box
					height={aspectHeight}
					style={style}
					width={aspectWidth}
					{...innerBoxProps}
				>
					{resolution ? (
						<Box
							height={byHeight ? resolution : (resolution * finalAspectRatio)}
							width={!byHeight ? resolution : (resolution * finalAspectRatio)}
						>
							{aspectBoxChildren}
						</Box>
					)
						:
						aspectBoxChildren
					}
				</Box>
			)}
		</Box>
	);
};

export default AspectBox;